import { useRef } from 'react';
import { VideoQuality } from '@zoom/videosdk';
import {
    MicrophoneIcon,
    VideoCameraIcon,
    VideoCameraSlashIcon,
    HandRaisedIcon,
    SignalIcon,
    SignalSlashIcon,
} from '@heroicons/react/24/solid';
import { Tooltip } from '../../../components/smartTable/Tooltip';
import { useAttachedVideo } from '../../../hooks/useAttachedVideo';
import { useInView } from '../../../hooks/useInView';

const getInitials = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

/** Zoom reporta 0-5 por enlace; se toma el peor de subida/bajada. */
const resolveConnection = (quality) => {
    if (!quality) return { level: 'unknown', label: 'Conexión sin datos' };
    const levels = [quality.uplink, quality.downlink].filter((v) => typeof v === 'number');
    if (!levels.length) return { level: 'unknown', label: 'Conexión sin datos' };
    const worst = Math.min(...levels);
    if (worst <= 1) return { level: 'bad', label: 'Conexión inestable' };
    if (worst === 2) return { level: 'fair', label: 'Conexión regular' };
    return { level: 'good', label: 'Conexión buena' };
};

const StatusButton = ({
    as = 'span',
    on,
    tone = null,
    title,
    disabled = false,
    onClick,
    children,
}) => {
    const stateClass = tone
        ? `is-${tone}`
        : (on ? 'is-on' : 'is-off');
    const className = `live-card__ctl ${stateClass}`;
    if (as === 'button') {
        return (
            <Tooltip title={title}>
                <button
                    type="button"
                    className={className}
                    aria-label={title}
                    aria-pressed={on}
                    disabled={disabled}
                    onClick={onClick}
                >
                    {children}
                </button>
            </Tooltip>
        );
    }
    return (
        <Tooltip title={title}>
            <span className={className} aria-label={title} role="img">
                {children}
            </span>
        </Tooltip>
    );
};

export const LiveParticipantCard = ({
    participant,
    isSpeaking = false,
    handRaised = false,
    networkQuality = null,
    canManage = false,
    busy = false,
    onMute,
    onAskUnmute,
    onLowerHand,
}) => {
    const connection = resolveConnection(networkQuality);
    const roleLabel = participant.isHost
        ? 'Profesor'
        : participant.isManager
            ? 'Moderador'
            : 'Estudiante';
    const micOn = !participant.muted;
    const camOn = Boolean(participant.bVideoOn);
    const canToggleMic = canManage && !participant.isSelf;

    const mediaRef = useRef(null);
    const videoRef = useRef(null);
    const inView = useInView(mediaRef);
    const showVideo = camOn && inView;

    useAttachedVideo(videoRef, participant.userId, {
        enabled: showVideo,
        quality: VideoQuality.Video_180P,
        className: 'live-card__player',
    });

    return (
        <article
            className={[
                'live-card',
                isSpeaking ? 'is-speaking' : '',
                handRaised ? 'has-hand' : '',
            ].filter(Boolean).join(' ')}
        >
            <div className={`live-card__media${showVideo ? ' has-video' : ''}`} ref={mediaRef}>
                <div className="live-card__video" ref={videoRef} />
                {!showVideo ? (
                    <span className="live-card__avatar" aria-hidden="true">
                        {getInitials(participant.displayName)}
                    </span>
                ) : null}
                {handRaised ? (
                    <span className="live-card__hand">
                        <Tooltip title="Tiene la mano levantada">
                            <span className="live-card__hand-icon">
                                <HandRaisedIcon aria-hidden="true" />
                                <span className="live-card__sr">Mano levantada</span>
                            </span>
                        </Tooltip>
                    </span>
                ) : null}
            </div>

            <div className="live-card__body">
                <p className="live-card__name">
                    {participant.displayName}
                    {participant.isSelf ? <span className="live-card__you"> (Tú)</span> : null}
                </p>
                <p className="live-card__role">{roleLabel}</p>
            </div>

            <div className="live-card__controls">
                <StatusButton
                    as={canToggleMic ? 'button' : 'span'}
                    on={micOn}
                    disabled={busy}
                    title={
                        canToggleMic
                            ? (micOn
                                ? 'Silenciar'
                                : 'Pedir activar micrófono (requiere consentimiento)')
                            : (micOn ? 'Micrófono activo' : 'Micrófono silenciado')
                    }
                    onClick={() => {
                        if (!canToggleMic) return;
                        if (micOn) onMute?.(participant.userId);
                        else onAskUnmute?.(participant.userId);
                    }}
                >
                    <MicrophoneIcon aria-hidden="true" />
                </StatusButton>

                <StatusButton
                    on={camOn}
                    title={camOn ? 'Cámara activa' : 'Cámara apagada'}
                >
                    {camOn
                        ? <VideoCameraIcon aria-hidden="true" />
                        : <VideoCameraSlashIcon aria-hidden="true" />}
                </StatusButton>

                <StatusButton
                    on={false}
                    tone={connection.level}
                    title={connection.label}
                >
                    {connection.level === 'bad'
                        ? <SignalSlashIcon aria-hidden="true" />
                        : <SignalIcon aria-hidden="true" />}
                </StatusButton>

                {canManage && !participant.isSelf && handRaised ? (
                    <Tooltip title="Bajar mano">
                        <button
                            type="button"
                            className="live-card__ctl is-hand"
                            aria-label="Bajar mano"
                            disabled={busy}
                            onClick={() => onLowerHand?.(participant.userId)}
                        >
                            <HandRaisedIcon aria-hidden="true" />
                        </button>
                    </Tooltip>
                ) : null}
            </div>
        </article>
    );
};
